import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { classifyAdminFailure, isAbortError, type AdminFailure } from './api';
import { READ_MEMORY_FRESH_MS, forgetRead, recallRead, rememberRead } from './readMemory';

export interface AdminRead<T> {
  /** The last answer received, possibly for an earlier key while the current one loads. */
  data: T | null;
  /** The key `data` belongs to; equal to the current key once its answer arrived. */
  dataKey: string | null;
  loading: boolean;
  failure: AdminFailure | null;
  /** Reads the current key again (after a failure). */
  retry: () => void;
}

interface State<T> {
  data: T | null;
  dataKey: string | null;
  loading: boolean;
  failure: AdminFailure | null;
}

/**
 * One staff read per key. A new key aborts the request of the previous one, so a slow answer to an
 * older search or page can never replace a newer one. The previous answer stays available while the new
 * one loads (pages keep showing it, marked busy) and is dropped on a failure, so stale rows are never
 * shown next to an error. A null key reads nothing (for example an address that cannot be an
 * organization).
 *
 * `remember` (the Apps pages) keeps the last answer under that name between visits (readMemory.ts): the page opens on
 * it at once, and its opening read is skipped while that answer is younger than READ_MEMORY_FRESH_MS.
 */
export function useAdminRead<T>(
  key: string | null,
  read: (signal: AbortSignal) => Promise<T>,
  remember?: string,
): AdminRead<T> {
  const [state, setState] = useState<State<T>>(() => {
    const kept = key !== null && remember ? recallRead<T>(remember) : undefined;
    return kept
      ? { data: kept.value, dataKey: key, loading: false, failure: null }
      : { data: null, dataKey: null, loading: key !== null, failure: null };
  });
  const [attempt, setAttempt] = useState(0);
  const runRead = useEffectEvent(read);
  // The key the page opened with: only its read (not Check again, a retry or another key) may be served from memory.
  const openingKey = useRef(key);

  useEffect(() => {
    if (key === null) {
      setState({ data: null, dataKey: null, loading: false, failure: null });
      return;
    }
    const kept =
      remember && attempt === 0 && key === openingKey.current ? recallRead<T>(remember) : undefined;
    if (kept && Date.now() - kept.at < READ_MEMORY_FRESH_MS) return;
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, failure: null }));
    runRead(controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        if (remember) rememberRead(remember, data);
        setState({ data, dataKey: key, loading: false, failure: null });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        // A failed read never leaves an answer to be shown on the next visit.
        if (remember) forgetRead(remember);
        setState({ data: null, dataKey: null, loading: false, failure: classifyAdminFailure(error) });
      },
    );
    return () => controller.abort();
  }, [key, attempt, remember]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
